// Dummy data for development mode
// This data is used when the app is running in dev mode to display sample data
// without requiring a database connection

export interface DummyServer {
  id: string;
  name: string;
  location_id: string;
  location_name: string;
  features: any;
  demo: boolean;
  test: boolean;
}

export interface DummyShift {
  shift_id: string;
  server_id: string;
  shift_created: string;
  shift_ended: string;
  interactions_count: number;
  duration_minutes: number;
}

export interface DummyInteraction {
  id: string;
  data: any;
  start_time: number;
  end_time: number;
  shift_id: string;
  interaction_type: string;
}

export interface DummyServerStats {
  server_id: string;
  server_name: string;
  location_id: string;
  location_name: string;
  total_shifts: number;
  total_interactions: number;
  total_duration_minutes: number;
  initial_greeting_count: number;
  server_created: string;
  criteria_stats: {
    upsell_count: number;
    mention_count: number;
    describe_count: number;
    drink_promotion_count: number;
  };
  premium_stats: {
    regular_sales: number;
    premium_sales: number;
    premium_conversion_rate: number;
  };
  shifts: DummyShift[];
}

// Sample servers data
export const dummyServers: DummyServer[] = [
  {
    id: 'server-1',
    name: 'Alice Johnson',
    location_id: 'location-1',
    location_name: 'Downtown Location',
    features: { notifications: true, trial_focus: true },
    demo: false,
    test: false
  },
  {
    id: 'server-2',
    name: 'Bob Smith',
    location_id: 'location-1',
    location_name: 'Downtown Location',
    features: { notifications: false, trial_focus: false },
    demo: false,
    test: false
  },
  {
    id: 'server-3',
    name: 'Carol Davis',
    location_id: 'location-2',
    location_name: 'Uptown Location',
    features: { notifications: true, trial_focus: true },
    demo: false,
    test: false
  },
  {
    id: 'server-4',
    name: 'David Wilson',
    location_id: 'location-2',
    location_name: 'Uptown Location',
    features: { notifications: true, trial_focus: false },
    demo: false,
    test: false
  }
];

// Sample locations data
// feat-094-training-progress-go-live-cutoff: location rows carry an optional
// `bespoke_go_live_date` (ISO YYYY-MM-DD or null). A non-null example exercises
// the "live" state and a null example exercises the "Not yet live" empty state
// so `start:fake` renders both branches of the Edit Location field.
// feat-320: `recording_start_date` likewise covers set, recording-before-training
// (Uptown) and null ("Not yet recording", Concord).
//
// feat-303-curriculum-drip-release: `drip_enabled` / `drip_lessons_per_day` are
// the per-location drip OVERRIDE (null = inherit from tenant). The four rows
// cover inherit, override-on with its own rate, override-off, and the Q4
// stray-rate case (enabled inherited, rate overridden).
export const dummyLocations = [
  { id: 'location-1', name: 'Downtown Location', recording_start_date: '2026-06-16', bespoke_go_live_date: '2026-06-16', drip_enabled: null, drip_lessons_per_day: null },
  { id: 'location-2', name: 'Uptown Location', recording_start_date: '2026-10-14', bespoke_go_live_date: null, drip_enabled: true, drip_lessons_per_day: 2 },
  { id: '22222222-2222-2222-2222-222222222222', name: 'Fremont', recording_start_date: '2026-06-16', bespoke_go_live_date: '2026-06-18', drip_enabled: false, drip_lessons_per_day: null },
  { id: '33333333-3333-3333-3333-333333333333', name: 'Concord', recording_start_date: null, bespoke_go_live_date: null, drip_enabled: null, drip_lessons_per_day: 3 }
];

// Sample shifts data
export const dummyShifts: DummyShift[] = [
  {
    shift_id: 'shift-1',
    server_id: 'server-1',
    shift_created: '2024-01-15T10:00:00Z',
    shift_ended: '2024-01-15T18:00:00Z',
    interactions_count: 25,
    duration_minutes: 480
  },
  {
    shift_id: 'shift-2',
    server_id: 'server-1',
    shift_created: '2024-01-16T10:00:00Z',
    shift_ended: '2024-01-16T18:00:00Z',
    interactions_count: 30,
    duration_minutes: 480
  },
  {
    shift_id: 'shift-3',
    server_id: 'server-2',
    shift_created: '2024-01-15T11:00:00Z',
    shift_ended: '2024-01-15T19:00:00Z',
    interactions_count: 20,
    duration_minutes: 480
  },
  {
    shift_id: 'shift-4',
    server_id: 'server-3',
    shift_created: '2024-01-15T12:00:00Z',
    shift_ended: '2024-01-15T20:00:00Z',
    interactions_count: 35,
    duration_minutes: 480
  }
];

// Sample interactions data
export const dummyInteractions: DummyInteraction[] = [
  {
    id: 'interaction-1',
    data: {
      service_criteria: [
        { type: 'upsell_premium_menu', fulfilled: true },
        { type: 'mention_specific_premium_item', fulfilled: true }
      ]
    },
    start_time: 1642248000,
    end_time: 1642248060,
    shift_id: 'shift-1',
    interaction_type: 'greeter_greeting'
  },
  {
    id: 'interaction-2',
    data: {
      service_criteria: [
        { type: 'describe_specific_premium_item', fulfilled: true }
      ]
    },
    start_time: 1642248200,
    end_time: 1642248260,
    shift_id: 'shift-1',
    interaction_type: 'drink_promotion'
  },
  {
    id: 'interaction-3',
    data: {
      service_criteria: [
        { type: 'upsell_premium_menu', fulfilled: false }
      ]
    },
    start_time: 1642248400,
    end_time: 1642248460,
    shift_id: 'shift-2',
    interaction_type: 'greeter_greeting'
  }
];

// Sample server stats data
export const dummyServerStats: DummyServerStats[] = [
  {
    server_id: 'server-1',
    server_name: 'Alice Johnson',
    location_id: 'location-1',
    location_name: 'Downtown Location',
    total_shifts: 2,
    total_interactions: 55,
    total_duration_minutes: 960, // 16 hours
    initial_greeting_count: 2,
    server_created: '2024-09-15T10:30:00Z', // 10:30 AM UTC (Lunch)
    criteria_stats: {
      upsell_count: 3,
      mention_count: 2,
      describe_count: 1,
      drink_promotion_count: 1
    },
    premium_stats: {
      regular_sales: 45,
      premium_sales: 12,
      premium_conversion_rate: 26.67
    },
    shifts: dummyShifts.filter(s => s.server_id === 'server-1')
  },
  {
    server_id: 'server-2',
    server_name: 'Bob Smith',
    location_id: 'location-1',
    location_name: 'Downtown Location',
    total_shifts: 1,
    total_interactions: 20,
    total_duration_minutes: 480, // 8 hours
    initial_greeting_count: 1,
    server_created: '2024-09-15T18:00:00Z', // 6:00 PM UTC (Dinner)
    criteria_stats: {
      upsell_count: 1,
      mention_count: 0,
      describe_count: 0,
      drink_promotion_count: 0
    },
    premium_stats: {
      regular_sales: 20,
      premium_sales: 3,
      premium_conversion_rate: 15.0
    },
    shifts: dummyShifts.filter(s => s.server_id === 'server-2')
  },
  {
    server_id: 'server-3',
    server_name: 'Carol Davis',
    location_id: 'location-2',
    location_name: 'Uptown Location',
    total_shifts: 1,
    total_interactions: 35,
    total_duration_minutes: 480, // 8 hours
    initial_greeting_count: 1,
    server_created: '2024-09-15T14:30:00Z', // 2:30 PM UTC (Lunch)
    criteria_stats: {
      upsell_count: 2,
      mention_count: 1,
      describe_count: 1,
      drink_promotion_count: 1
    },
    premium_stats: {
      regular_sales: 35,
      premium_sales: 8,
      premium_conversion_rate: 22.86
    },
    shifts: dummyShifts.filter(s => s.server_id === 'server-3')
  }
];

// Sample trial analysis data
export const dummyTrialData = {
  serverName: 'Alice Johnson',
  baselineStats: {
    totalInteractions: 25,
    upsellCount: 2,
    mentionCount: 1,
    describeCount: 0,
    drinkPromotionCount: 0,
    initialGreetingCount: 1
  },
  overallTrialStats: {
    totalInteractions: 55,
    upsellCount: 3,
    mentionCount: 2,
    describeCount: 1,
    drinkPromotionCount: 1,
    initialGreetingCount: 2
  },
  dailyStats: [
    {
      date: '2024-01-15',
      totalInteractions: 25,
      upsellCount: 2,
      mentionCount: 1,
      describeCount: 0,
      drinkPromotionCount: 0,
      initialGreetingCount: 1
    },
    {
      date: '2024-01-16',
      totalInteractions: 30,
      upsellCount: 1,
      mentionCount: 1,
      describeCount: 1,
      drinkPromotionCount: 1,
      initialGreetingCount: 1
    }
  ]
};

// Helper function to simulate API response format
export const createDummyApiResponse = (data: any) => ({
  results: data
});

// Helper function to simulate SQL query results
export const getDummyDataForQuery = (sql: string): any[] => {
  const lowerSql = sql.toLowerCase();
  
  if (lowerSql.includes('from server') && lowerSql.includes('join location')) {
    return dummyServers;
  }
  
  if (lowerSql.includes('from location')) {
    return dummyLocations;
  }
  
  if (lowerSql.includes('from shift')) {
    return dummyShifts;
  }
  
  if (lowerSql.includes('from shift_interaction')) {
    return dummyInteractions;
  }
  
  if (lowerSql.includes('from revel_server') || lowerSql.includes('from revel_order')) {
    // Return empty array for revel data in dev mode
    return [];
  }
  
  // Default fallback
  return [];
};

// Dummy data for server performance history
export const dummyServerPerformanceHistoryData = [
  {
    date: '2024-09-15',
    shiftId: 'shift-1',
    startTime: '2024-09-15T10:00:00Z',
    endTime: '2024-09-15T18:00:00Z',
    duration: 480,
    interactions: 8,
    greetings: 5,
    score: 85,
    premiumMenu: 80,
    itemMention: 80,
    itemDescription: 60,
    drinkPromotions: 100,
    type: 'lunch'
  },
  {
    date: '2024-09-14',
    shiftId: 'shift-2',
    startTime: '2024-09-14T11:00:00Z',
    endTime: '2024-09-14T19:00:00Z',
    duration: 480,
    interactions: 7,
    greetings: 4,
    score: 75,
    premiumMenu: 75,
    itemMention: 75,
    itemDescription: 75,
    drinkPromotions: 75,
    type: 'lunch'
  },
  {
    date: '2024-09-13',
    shiftId: 'shift-3',
    startTime: '2024-09-13T12:00:00Z',
    endTime: '2024-09-13T20:00:00Z',
    duration: 480,
    interactions: 6,
    greetings: 3,
    score: 90,
    premiumMenu: 100,
    itemMention: 100,
    itemDescription: 100,
    drinkPromotions: 67,
    type: 'lunch'
  },
  {
    date: '2024-09-12',
    shiftId: 'shift-4',
    startTime: '2024-09-12T10:30:00Z',
    endTime: '2024-09-12T18:30:00Z',
    duration: 480,
    interactions: 9,
    greetings: 6,
    score: 80,
    premiumMenu: 83,
    itemMention: 83,
    itemDescription: 83,
    drinkPromotions: 67,
    type: 'lunch'
  },
  {
    date: '2024-09-11',
    shiftId: 'shift-5',
    startTime: '2024-09-11T09:00:00Z',
    endTime: '2024-09-11T17:00:00Z',
    duration: 480,
    interactions: 10,
    greetings: 7,
    score: 88,
    premiumMenu: 86,
    itemMention: 86,
    itemDescription: 86,
    drinkPromotions: 86,
    type: 'lunch'
  }
];
